import { useEffect, useState } from 'react';
import { SWAGGER_URL, apiUrl } from '../../shared/api/apiConfig';
import { useAuth } from '../../shared/auth/AuthContext';
import { getUserDisplayName } from '../../shared/auth/userDisplayName';
import { formatDisplayDateTime } from '../../shared/utils/dateFormat';
import './WeighingRequestsPage.css';

const EMPTY_FORM = {
  registerId: '',
  requestDirection: 'Outbound',
  linkedOutboundLoadId: '',
  source: 'user',
};

function sortByRegisterId(items) {
  return [...items].sort((left, right) => left.registerId.localeCompare(right.registerId));
}

function sortRequests(items) {
  return [...items].sort((left, right) => new Date(right.issuedAtUtc) - new Date(left.issuedAtUtc));
}

function isActiveRequest(request) {
  return request.requestStatus !== 'Completed' && request.requestStatus !== 'Cancelled';
}

function canRejectRequest(request) {
  return request.requestStatus !== 'Cancelled'
    && request.confirmationStatus !== 'Confirmed'
    && request.confirmationStatus !== 'Rejected';
}

function formatDate(value) {
  return formatDisplayDateTime(value, 'Unknown');
}

function buildDeviceMap(devices) {
  return new Map(devices.map((device) => [device.deviceId, device]));
}

function deviceSummary(device) {
  if (!device) {
    return 'Missing device';
  }

  return `${device.deviceId} · ${device.name} · ${device.deviceStatus}`;
}

function outboundRequestSummary(request) {
  return `${request.requestLoadId ?? request.requestId} Â· ${request.registerId} Â· ${request.requestStatus}`;
}

function outboundRequestOptionLabel(request) {
  return `${request.requestLoadId ?? request.requestId} - ${request.registerId} - ${request.requestStatus}`;
}

function requestHeadingLabel(request) {
  return `Register ${request.registerId} - ${request.requestDirection}${request.requestLoadId ? ` - load ${request.requestLoadId}` : ''}`;
}

function buildRegisterCards(bindings, devices, requests) {
  const deviceMap = buildDeviceMap(devices);
  const activeRequestByRegisterId = new Map();
  const latestRequestByRegisterId = new Map();

  for (const request of sortRequests(requests)) {
    if (!latestRequestByRegisterId.has(request.registerId)) {
      latestRequestByRegisterId.set(request.registerId, request);
    }

    if (isActiveRequest(request) && !activeRequestByRegisterId.has(request.registerId)) {
      activeRequestByRegisterId.set(request.registerId, request);
    }
  }

  return sortByRegisterId(bindings).map((binding) => {
    const scanner = deviceMap.get(binding.scannerDeviceId);
    const scale = deviceMap.get(binding.scaleDeviceId);
    const activeRequest = activeRequestByRegisterId.get(binding.registerId) ?? null;
    const latestRequest = latestRequestByRegisterId.get(binding.registerId) ?? null;
    const blockers = [];

    if (binding.status !== 'Active') {
      blockers.push(`Binding is ${String(binding.status).toLowerCase()}.`);
    }

    if (!scanner) {
      blockers.push(`Scanner '${binding.scannerDeviceId}' is missing.`);
    } else if (scanner.deviceStatus !== 'Active') {
      blockers.push(`Scanner '${scanner.deviceId}' is ${String(scanner.deviceStatus).toLowerCase()}.`);
    }

    if (!scale) {
      blockers.push(`Scale '${binding.scaleDeviceId}' is missing.`);
    } else if (scale.deviceStatus !== 'Active') {
      blockers.push(`Scale '${scale.deviceId}' is ${String(scale.deviceStatus).toLowerCase()}.`);
    }

    if (activeRequest) {
      blockers.push(`Active request '${activeRequest.requestId}' is still open.`);
    }

    if (
      !activeRequest &&
      latestRequest?.requestStatus === 'Completed' &&
      latestRequest?.confirmationStatus !== 'Confirmed'
    ) {
      blockers.push(`Latest request '${latestRequest.requestId}' is not confirmed yet.`);
    }

    return {
      ...binding,
      scanner,
      scale,
      activeRequest,
      latestRequest,
      blockers,
      canCreateRequest: blockers.length === 0,
    };
  });
}

function statusClass(value) {
  return String(value ?? 'unknown').toLowerCase().replace(/\s+/g, '-');
}

export default function WeighingRequestsPage() {
  const { user } = useAuth();
  const resolvedActorName = getUserDisplayName(user);
  const actorName = resolvedActorName || 'weighing-page';
  const actorSource = resolvedActorName ? `user:${resolvedActorName}` : 'user';
  const [devices, setDevices] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState(() => ({ ...EMPTY_FORM, source: actorSource }));
  const [loadState, setLoadState] = useState({ type: 'idle', message: '' });
  const [saveState, setSaveState] = useState({ type: 'idle', message: '' });
  const [requestActionState, setRequestActionState] = useState({ type: 'idle', message: '' });
  const [pendingRequestId, setPendingRequestId] = useState('');

  useEffect(() => {
    setForm((current) => (
      current.source === actorSource
        ? current
        : { ...current, source: actorSource }
    ));
  }, [actorSource]);

  useEffect(() => {
    let active = true;

    async function loadPageData() {
      setLoadState({ type: 'pending', message: 'Loading registers, devices, and requests...' });

      try {
        const [devicesResponse, bindingsResponse, requestsResponse] = await Promise.all([
          fetch(apiUrl('/devices')),
          fetch(apiUrl('/registerbindings')),
          fetch(apiUrl('/weighingrequests?take=150')),
        ]);

        if (!devicesResponse.ok || !bindingsResponse.ok || !requestsResponse.ok) {
          throw new Error('Failed to load weighing request data.');
        }

        const [devicesPayload, bindingsPayload, requestsPayload] = await Promise.all([
          devicesResponse.json(),
          bindingsResponse.json(),
          requestsResponse.json(),
        ]);

        if (!active) {
          return;
        }

        setDevices(devicesPayload ?? []);
        setBindings(bindingsPayload ?? []);
        setRequests(sortRequests(requestsPayload ?? []));
        setLoadState({ type: 'success', message: '' });
      } catch (error) {
        if (!active) {
          return;
        }

        setLoadState({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to load weighing request data.',
        });
      }
    }

    loadPageData();

    return () => {
      active = false;
    };
  }, []);

  const registerCards = buildRegisterCards(bindings, devices, requests);
  const readyRegisters = registerCards.filter((card) => card.canCreateRequest);
  const blockedRegisters = registerCards.filter((card) => !card.canCreateRequest);
  const activeRequests = sortRequests(requests.filter(isActiveRequest));
  const recentRequests = sortRequests(requests).slice(0, 24);
  const outboundRequests = sortRequests(
    requests.filter((request) => request.requestDirection === 'Outbound' && request.requestLoadId),
  );

  useEffect(() => {
    if (readyRegisters.length === 0) {
      if (form.registerId) {
        setForm((current) => ({ ...current, registerId: '' }));
      }
      return;
    }

    if (!readyRegisters.some((item) => item.registerId === form.registerId)) {
      setForm((current) => ({ ...current, registerId: readyRegisters[0].registerId }));
    }
  }, [form.registerId, readyRegisters]);

  useEffect(() => {
    if (form.requestDirection !== 'Inbound') {
      return;
    }

    if (outboundRequests.length === 0) {
      if (form.linkedOutboundLoadId) {
        setForm((current) => ({ ...current, linkedOutboundLoadId: '' }));
      }
      return;
    }

    if (!outboundRequests.some((request) => request.requestLoadId === form.linkedOutboundLoadId)) {
      setForm((current) => ({ ...current, linkedOutboundLoadId: outboundRequests[0].requestLoadId }));
    }
  }, [form.linkedOutboundLoadId, form.requestDirection, outboundRequests]);

  function handleFieldChange(event) {
    const { name, value } = event.target;
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === 'requestDirection' && value !== 'Inbound') {
        next.linkedOutboundLoadId = '';
      }
      return next;
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const payload = {
      registerId: form.registerId,
      requestDirection: form.requestDirection,
      linkedOutboundLoadId: form.requestDirection === 'Inbound'
        ? (form.linkedOutboundLoadId || null)
        : null,
      source: form.source.trim() || 'user',
    };

    if (!payload.registerId) {
      setSaveState({ type: 'error', message: 'Choose a ready register before creating a request.' });
      return;
    }

    if (payload.requestDirection === 'Inbound' && !payload.linkedOutboundLoadId) {
      setSaveState({ type: 'error', message: 'Choose an outbound load before creating an inbound request.' });
      return;
    }

    setSaveState({ type: 'pending', message: 'Creating the next weighing request...' });

    try {
      const response = await fetch(apiUrl('/weighingrequests'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message ?? 'Unable to create the request.');
      }

      const created = await response.json();
      setRequests((current) => sortRequests([created, ...current.filter((item) => item.requestId !== created.requestId)]));
      setSaveState({
        type: 'success',
        message: `Request '${created.requestId}' is now active for register '${created.registerId}'. Scanner and scale devices can start working on it immediately.`,
      });
    } catch (error) {
      setSaveState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to create the request.',
      });
    }
  }

  async function handleRejectRequest(request) {
    const confirmed = window.confirm(
      `Reject request '${request.requestId}' and release register '${request.registerId}' for the next workflow?`,
    );
    if (!confirmed) {
      return;
    }

    const rejectionNotes = window.prompt(
      `Optional rejection note for '${request.requestId}':`,
      request.confirmationNotes ?? '',
    );
    if (rejectionNotes === null) {
      return;
    }

    setPendingRequestId(request.requestId);
    setRequestActionState({ type: 'pending', message: `Rejecting '${request.requestId}'...` });

    try {
      const response = await fetch(apiUrl(`/weighingrequests/${encodeURIComponent(request.requestId)}/reject`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rejectedBy: actorName,
          rejectionNotes,
          source: actorSource,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.message ?? 'Unable to reject the request.');
      }

      const updated = await response.json();
      setRequests((current) => sortRequests([updated, ...current.filter((item) => item.requestId !== updated.requestId)]));
      setRequestActionState({
        type: 'success',
        message: `Request '${updated.requestId}' was rejected and the register is no longer blocked by it.`,
      });
    } catch (error) {
      setRequestActionState({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to reject the request.',
      });
    } finally {
      setPendingRequestId('');
    }
  }

  return (
    <section className="weighing-requests-page">
      <div className="weighing-requests-hero">
        <div>
          <p className="weighing-requests-eyebrow">Request Control</p>
          <h1>Manage weighing requests</h1>
          <p className="weighing-requests-subtitle">
            Create the next request for a ready register, review blocked workflow lanes, and close out requests that
            should no longer hold the scanner and scale.
          </p>
        </div>

        <div className="weighing-requests-hero-side">
          <div className="weighing-requests-summary">
            <div>
              <strong>{readyRegisters.length}</strong>
              <span>ready registers</span>
            </div>
            <div>
              <strong>{activeRequests.length}</strong>
              <span>active requests</span>
            </div>
            <div>
              <strong>{recentRequests.length}</strong>
              <span>recent shown</span>
            </div>
          </div>

          <a
            href={SWAGGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="weighing-requests-secondary-btn"
          >
            Open API Docs (Swagger)
          </a>
        </div>
      </div>

      <div className="weighing-requests-grid">
        <div className="weighing-requests-stack">
          <form className="weighing-requests-card weighing-requests-form-card" onSubmit={handleSubmit}>
            <div className="weighing-requests-card-header">
              <div>
                <h2>New request</h2>
                <p>The server generates the next `RequestId` for the selected register.</p>
              </div>
            </div>

            <div className="weighing-requests-rule">
              Creation rule: a register can start a new request only when its scanner and scale are both active
              and the previous request is no longer blocking the workflow.
            </div>

            <label className="weighing-requests-field">
              <span>Register</span>
              <select name="registerId" value={form.registerId} onChange={handleFieldChange} disabled={readyRegisters.length === 0}>
                <option value="">{readyRegisters.length === 0 ? 'No ready register available' : 'Select register'}</option>
                {readyRegisters.map((register) => (
                  <option key={register.registerId} value={register.registerId}>
                    {register.registerId} · {register.scannerDeviceId} / {register.scaleDeviceId}
                  </option>
                ))}
              </select>
            </label>

            <label className="weighing-requests-field">
              <span>Request Direction</span>
              <select name="requestDirection" value={form.requestDirection} onChange={handleFieldChange}>
                <option value="Outbound">Outbound</option>
                <option value="Inbound">Inbound</option>
              </select>
            </label>

            {form.requestDirection === 'Inbound' ? (
              <label className="weighing-requests-field">
                <span>Linked Outbound Load</span>
                <select
                  name="linkedOutboundLoadId"
                  value={form.linkedOutboundLoadId}
                  onChange={handleFieldChange}
                  disabled={outboundRequests.length === 0}
                >
                  <option value="">
                    {outboundRequests.length === 0 ? 'No outbound load available' : 'Select outbound load'}
                  </option>
                  {outboundRequests.map((request) => (
                    <option key={request.requestId} value={request.requestLoadId}>
                      {outboundRequestOptionLabel(request)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}

            <label className="weighing-requests-field">
              <span>Source</span>
              <input
                name="source"
                value={form.source}
                readOnly
                placeholder="e.g. user, user:warehouse-admin"
              />
            </label>

            <div className="weighing-requests-actions">
              <button
                type="submit"
                className="weighing-requests-primary-btn"
                disabled={readyRegisters.length === 0 || saveState.type === 'pending'}
              >
                Create request
              </button>
            </div>

            {saveState.message ? (
              <p className={`weighing-requests-feedback weighing-requests-feedback-${saveState.type}`}>
                {saveState.message}
              </p>
            ) : null}
          </form>

          <div className="weighing-requests-card">
            <div className="weighing-requests-card-header">
              <div>
                <h2>Ready registers</h2>
                <p>These bindings can receive the next request immediately.</p>
              </div>
            </div>

            {readyRegisters.length === 0 ? (
              <div className="weighing-requests-empty">
                <strong>No register is ready right now</strong>
                <p>Check device status, active requests, or pending confirmations below.</p>
              </div>
            ) : (
              <div className="weighing-requests-register-list">
                {readyRegisters.map((register) => (
                  <article key={register.registerId} className="weighing-requests-register-card">
                    <div className="weighing-requests-register-top">
                      <div>
                        <h3>{register.registerId}</h3>
                        <p>Created {formatDate(register.createdAtUtc)}</p>
                      </div>
                      <span className="weighing-requests-pill weighing-requests-pill-ready">Ready</span>
                    </div>

                    <dl className="weighing-requests-meta-grid">
                      <div>
                        <dt>Scanner</dt>
                        <dd>{deviceSummary(register.scanner)}</dd>
                      </div>
                      <div>
                        <dt>Scale</dt>
                        <dd>{deviceSummary(register.scale)}</dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="weighing-requests-stack">
          <div className="weighing-requests-card">
            <div className="weighing-requests-card-header">
              <div>
                <h2>Blocked registers</h2>
                <p>These bindings need attention before another request can start.</p>
              </div>
            </div>

            {loadState.type === 'error' ? (
              <div className="weighing-requests-empty">
                <strong>Unable to load request data</strong>
                <p>{loadState.message}</p>
              </div>
            ) : blockedRegisters.length === 0 ? (
              <div className="weighing-requests-empty">
                <strong>No blocked registers</strong>
                <p>Every current binding is either ready or not yet created.</p>
              </div>
            ) : (
              <div className="weighing-requests-register-list">
                {blockedRegisters.map((register) => (
                  <article key={register.registerId} className="weighing-requests-register-card weighing-requests-register-card-blocked">
                    <div className="weighing-requests-register-top">
                      <div>
                        <h3>{register.registerId}</h3>
                        <p>
                          {register.activeRequest
                            ? `Blocked by active request ${register.activeRequest.requestId}`
                            : 'Blocked by readiness or confirmation rules'}
                        </p>
                      </div>
                      <span className="weighing-requests-pill weighing-requests-pill-blocked">Blocked</span>
                    </div>

                    <ul className="weighing-requests-blockers">
                      {register.blockers.map((blocker) => (
                        <li key={blocker}>{blocker}</li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            )}
          </div>

          <div className="weighing-requests-card">
            <div className="weighing-requests-card-header">
              <div>
                <h2>Recent requests</h2>
                <p>Current and completed requests across the latest workflow activity.</p>
              </div>
            </div>

            {requestActionState.message ? (
              <p className={`weighing-requests-feedback weighing-requests-feedback-${requestActionState.type}`}>
                {requestActionState.message}
              </p>
            ) : null}

            {loadState.type === 'error' ? (
              <div className="weighing-requests-empty">
                <strong>Unable to load requests</strong>
                <p>{loadState.message}</p>
              </div>
            ) : recentRequests.length === 0 ? (
              <div className="weighing-requests-empty">
                <strong>No requests yet</strong>
                <p>Create the first weighing request from a ready register.</p>
              </div>
            ) : (
              <div className="weighing-requests-list">
                {recentRequests.map((request) => (
                  <article key={request.requestId} className="weighing-requests-item">
                    <div className="weighing-requests-item-top">
                      <div>
                        <h3>{request.requestId}</h3>
                        <p>{requestHeadingLabel(request)}</p>
                      </div>
                      <span className={`weighing-requests-status weighing-requests-status-${statusClass(request.requestStatus)}`}>
                        {request.requestStatus}
                      </span>
                    </div>

                    <dl className="weighing-requests-meta-grid">
                      <div>
                        <dt>Issued</dt>
                        <dd>{formatDate(request.issuedAtUtc)}</dd>
                      </div>
                      <div>
                        <dt>Completed</dt>
                        <dd>{formatDate(request.completedAtUtc)}</dd>
                      </div>
                      <div>
                        <dt>Confirmation</dt>
                        <dd>{request.confirmationStatus}</dd>
                      </div>
                      <div>
                        <dt>Request Load</dt>
                        <dd>{request.requestLoadId ?? 'Not assigned'}</dd>
                      </div>
                      <div>
                        <dt>Confirmed By</dt>
                        <dd>{request.confirmedBy || 'Pending'}</dd>
                      </div>
                      <div>
                        <dt>Scanner Result</dt>
                        <dd>{request.selectedScannerReadingId ?? 'Not selected'}</dd>
                      </div>
                      <div>
                        <dt>Scale Result</dt>
                        <dd>{request.selectedScaleReadingId ?? 'Not selected'}</dd>
                      </div>
                    </dl>

                    {canRejectRequest(request) ? (
                      <div className="weighing-requests-item-actions">
                        <button
                          type="button"
                          className="weighing-requests-danger-btn"
                          onClick={() => handleRejectRequest(request)}
                          disabled={pendingRequestId === request.requestId}
                        >
                          {pendingRequestId === request.requestId ? 'Rejecting...' : 'Reject request'}
                        </button>
                      </div>
                    ) : null}
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
